<template>
  <div class="analytics-dashboard">
    <div v-if="error" class="error-alert">
      <div class="error-icon">⚠️</div>
      <div class="error-content">
        <div class="error-title">Error Loading Data</div>
        <div class="error-message">{{ error }}</div>
        <button class="retry-button" @click="loadAll">Retry</button>
      </div>
    </div>
    
    <div class="dashboard-header">
      <h1 class="dashboard-title">{{ title }}</h1>
      <div class="dashboard-controls">
        <div class="date-range-picker">
          <label for="date-range">Date Range:</label>
          <select id="date-range" v-model="selectedDateRange" class="date-range-select">
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
            <option value="custom">Custom Range</option>
          </select>
          
          <div v-if="selectedDateRange === 'custom'" class="custom-date-range">
            <input 
              type="date" 
              v-model="customDateStart" 
              class="date-input"
              :max="today"
            />
            <span>to</span>
            <input 
              type="date" 
              v-model="customDateEnd" 
              class="date-input"
              :min="customDateStart"
              :max="today"
            />
          </div>
        </div>
        
        <button class="refresh-button" @click="loadAll" :disabled="isAnyLoading">
          <span class="refresh-icon" :class="{ 'spinning': isAnyLoading }">🔄</span>
          Refresh
        </button>
      </div>
    </div>
    
    <div class="dashboard-content">
      <slot 
        name="main"
        :dateRange="effectiveDateRange"
        :isLoading="isAnyLoading"
        :userData="userData"
        :courseData="courseData"
        :revenueData="revenueData"
        :assessmentData="assessmentData"
        :engagementData="engagementData"
      ></slot>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import axios from 'axios';

export default {
  name: 'AnalyticsDashboard',
  props: {
    title: {
      type: String,
      default: 'Analytics Dashboard'
    },
    baseApiUrl: {
      type: String,
      default: 'https://api.kewaar.com/api/admin/analytics'
    },
    autoRefresh: {
      type: Boolean,
      default: false
    },
    refreshInterval: {
      type: Number,
      default: 300000 // 5 minutes
    }
  },
  setup(props) {
    const today = new Date().toISOString().split('T')[0];
    
    // Date range selection
    const selectedDateRange = ref('30d');
    const customDateStart = ref(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const customDateEnd = ref(today);
    
    // Data stores
    const userData = ref({});
    const courseData = ref({});
    const revenueData = ref({});
    const assessmentData = ref({});
    const engagementData = ref({});
    
    // Loading states
    const isLoadingUser = ref(false);
    const isLoadingCourse = ref(false);
    const isLoadingRevenue = ref(false);
    const isLoadingAssessment = ref(false);
    const isLoadingEngagement = ref(false);
    
    // Error state
    const error = ref('');
    
    // Computed property to check if any data is loading
    const isAnyLoading = computed(() => {
      return isLoadingUser.value || 
             isLoadingCourse.value || 
             isLoadingRevenue.value || 
             isLoadingAssessment.value || 
             isLoadingEngagement.value;
    });
    
    // Compute effective date range based on selection
    const effectiveDateRange = computed(() => {
      const end = new Date();
      let start = new Date();
      
      switch (selectedDateRange.value) {
        case '7d':
          start.setDate(end.getDate() - 7);
          break;
        case '30d':
          start.setDate(end.getDate() - 30);
          break;
        case '90d':
          start.setDate(end.getDate() - 90);
          break;
        case '1y':
          start.setFullYear(end.getFullYear() - 1);
          break;
        case 'custom': {
          start = new Date(customDateStart.value);
          const customEndDate = new Date(customDateEnd.value);
          return {
            start,
            end: customEndDate,
            formattedStart: formatDate(start),
            formattedEnd: formatDate(customEndDate)
          };
        }
      }
      
      return {
        start,
        end,
        formattedStart: formatDate(start),
        formattedEnd: formatDate(end)
      };
    });
    
    // Format date for API requests
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };
    
    // Get auth token
    const getToken = () => {
      return localStorage.getItem('token');
    };
    
    // API request function with error handling
    const apiRequest = async (endpoint) => {
      const token = getToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      try {
        const { start, end } = effectiveDateRange.value;
        
        const response = await axios.get(`${props.baseApiUrl}/${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          params: {
            start_date: formatDate(start),
            end_date: formatDate(end)
          }
        });
        
        return response.data.data;
      } catch (err) {
        console.error(`Error fetching ${endpoint} data:`, err);
        
        if (err.response) {
          // Server responded with an error status
          if (err.response.status === 401) {
            throw new Error('Authentication failed. Please log in again.');
          } else {
            throw new Error(`Server error (${err.response.status}): ${err.response.data.message || 'Unknown error'}`);
          }
        } else if (err.request) {
          // Request made but no response
          throw new Error('Network error. Please check your connection.');
        } else {
          // Error setting up request
          throw new Error(`Request error: ${err.message}`);
        }
      }
    };
    
    // Data loading functions
    const loadUserData = async () => {
      isLoadingUser.value = true;
      error.value = '';
      
      try {
        userData.value = await apiRequest('users');
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoadingUser.value = false;
      }
    };
    
    const loadCourseData = async () => {
      isLoadingCourse.value = true;
      error.value = '';
      
      try {
        courseData.value = await apiRequest('courses');
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoadingCourse.value = false;
      }
    };
    
    const loadRevenueData = async () => {
      isLoadingRevenue.value = true;
      error.value = '';
      
      try {
        revenueData.value = await apiRequest('revenue');
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoadingRevenue.value = false;
      }
    };
    
    const loadAssessmentData = async () => {
      isLoadingAssessment.value = true;
      error.value = '';
      
      try {
        assessmentData.value = await apiRequest('assessments');
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoadingAssessment.value = false;
      }
    };
    
    const loadEngagementData = async () => {
      isLoadingEngagement.value = true;
      error.value = '';
      
      try {
        engagementData.value = await apiRequest('engagement');
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoadingEngagement.value = false;
      }
    };
    
    // Load all data
    const loadAll = async () => {
      error.value = '';
      
      await Promise.all([
        loadUserData(),
        loadCourseData(),
        loadRevenueData(),
        loadAssessmentData(),
        loadEngagementData()
      ]);
    };
    
    // Set up auto-refresh
    let refreshTimer = null;
    
    const setupAutoRefresh = () => {
      if (props.autoRefresh && props.refreshInterval > 0) {
        clearInterval(refreshTimer);
        refreshTimer = setInterval(loadAll, props.refreshInterval);
      }
    };
    
    // Watch for date range changes to reload data
    watch([effectiveDateRange], () => {
      loadAll();
    }, { deep: true });
    
    // Initial data load
    onMounted(() => {
      loadAll();
      setupAutoRefresh();
    });
    
    // Clean up on component unmount
    const onUnmounted = () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
    
    return {
      today,
      selectedDateRange,
      customDateStart,
      customDateEnd,
      userData,
      courseData,
      revenueData,
      assessmentData,
      engagementData,
      isLoadingUser,
      isLoadingCourse,
      isLoadingRevenue,
      isLoadingAssessment,
      isLoadingEngagement,
      isAnyLoading,
      effectiveDateRange,
      error,
      loadAll,
      onUnmounted
    };
  }
};
</script>

<style scoped>
.analytics-dashboard {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
}

.dashboard-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
}

.dashboard-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
}

.dashboard-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
}

.date-range-picker {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.date-range-picker label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.date-range-select,
.date-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: #f8fafc;
  color: #1e293b;
  transition: all 0.2s ease;
}

.date-range-select:focus,
.date-input:focus {
  outline: none;
  border-color: #93c5fd;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.custom-date-range {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.custom-date-range span {
  font-size: 0.875rem;
  color: #64748b;
}

.refresh-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-button:hover:not(:disabled) {
  background-color: #e2e8f0;
  color: #334155;
}

.refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-icon {
  display: inline-block;
  font-size: 1rem;
}

.refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-alert {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.error-icon {
  font-size: 1.5rem;
}

.error-content {
  flex: 1;
}

.error-title {
  font-weight: 600;
  color: #b91c1c;
  margin-bottom: 0.25rem;
}

.error-message {
  color: #ef4444;
  margin-bottom: 0.5rem;
}

.retry-button {
  padding: 0.25rem 0.75rem;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.retry-button:hover {
  background-color: #dc2626;
}

.dashboard-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .dashboard-controls {
    width: 100%;
    justify-content: space-between;
  }
  
  .date-range-picker {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .custom-date-range {
    flex-direction: column;
    width: 100%;
  }
  
  .date-input {
    width: 100%;
  }
  
  .refresh-button {
    width: 100%;
    justify-content: center;
  }
}
</style> 